/* eslint-disable max-len */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import IcomoonReact from 'icomoon-react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import Spinner from '../../../shared/components/Spinner/Spinner';
import iconSet from '../../../shared/images/teambuildr-selection.json';
import Text from '../../../shared/components/Text/Text';
import decodeHtmlEntities from '../../../shared/utils/decodeHtmlEntities';
import {
  logReduxPresaveAddInfo, logReduxPresaveDescription, updateProgrammedCalendarItem, swapProgrammedItemOnChildCalendar,
  updateSavedWorkoutItem,
} from '../ducks/calendarActions';

const ModalContainer = styled('div')`
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
`;

const ModalTextTop = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 24px;
  font-weight: 700;
`;

const TextArea = styled('textarea')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  width: 100%;
  margin-top: 10px;
  border: 3px solid #E0E0E0;
  border-radius: 5px;
  flex-grow: 1;
  padding: 10px;
  min-height: 200px;
`;

const Subtext = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 400;
  margin-top: 5px;
  color: #9E9E9E;
`;

const ExerciseTitle = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 600;
  margin-top: 2px;
  color: #444444;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
`;

const TextContainer = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const ButtonsContainer = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
  flex-shrink: 0;
`;

const TopTextAndX = styled('div')`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const NewCalendarButton = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 400;
  width: 100%;
  height: 32px;
  padding: 6px 16px 6px 16px;
  background: #444444;
  color: white;
  border-radius: 4px;
  margin-top: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 700;
  user-select: none;
  border: 1px solid #444444;
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  pointer-events: ${(props) => (props.disabled ? 'none' : 'auto')};
`;

const ErrorText = styled(Text)`
  color: #E53935;
  font-size: 12px;
  margin-top: 8px;
`;

const ManageCalendarsButton = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 400;
  width: 100%;
  height: 32px;
  padding: 6px 16px 6px 16px;
  color: #444444;
  border-radius: 4px;
  margin-top: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 700;
  cursor: pointer;
  user-select: none;
  border: 1px solid #444444;
`;

const RevertTextContainer = styled('div')`
display: flex;
width: 100%;
justify-content: flex-end;
gap: 4px;
align-items: center;
margin-top: 5px;
transition: all 250ms ease-in-out;
cursor: pointer;
  &:hover {
    opacity: 0.7;
  }
`;

const SpinnerWrapper = styled('div')`
display: flex;
min-height: 229px;

`;

const AddDescriptionModal = ({
  closeModal,
}) => {
  const [text, setText] = useState('');
  const activeModal = useSelector((state) => state.calendar.data.activeModal);
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const modalData = useSelector((state) => state.calendar.data.modalData);
  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);
  const preSaveDescription = useSelector((state) => state.calendar.data.preSaveDescription);
  const preSaveAdditionalInfo = useSelector((state) => state.calendar.data.preSaveAddInfo);
  const isCalendarItemUpdating = useSelector((state) => state.calendar.ui.isCalendarItemUpdating);
  const programType = useSelector((state) => state.calendar.data.programType);
  const calendarItems = useSelector((state) => state.calendar.data.calendarItems);
  const nonDateBasedProgramItems = useSelector((state) => state.calendar.data.nonDateBasedProgramItems);

  const requiresDescription = activeModal === 'addDescriptionModal'
    && ['note', 'warm_up', 'cooldown'].includes(modalData?.type);
  const isTextEmpty = !text || text.trim() === '';
  const showRequiredError = requiresDescription && isTextEmpty;

  useEffect(() => {
    if (activeModal === 'addDescriptionModal') {
      const matchedInfo = preSaveDescription?.find((desc) => desc.id === modalData?.id);
      if (modalData.preSave && matchedInfo) {
        setText(decodeHtmlEntities(matchedInfo.content));
      } else {
        setText(decodeHtmlEntities(modalData?.description || modalData?.exercise?.description));
      }
    }

    if (activeModal === 'addAdditionalInfoModal') {
      const matchedInfo = preSaveAdditionalInfo?.find((info) => info.id === modalData?.id);
      if (modalData.preSave && matchedInfo) {
        setText(decodeHtmlEntities(matchedInfo.content));
      } else {
        setText(decodeHtmlEntities(modalData?.additionalInfo || modalData?.exercise?.additionalInfo));
      }
    }
  }, [modalData]);

  const dispatch = useDispatch();

  const formatDate = () => `${moment(modalData?.effectiveDate || selectedDate).format('YYYY-MM-DD')}`;

  const handleSave = () => {
    if (!modalData.preSave) {
      const nonSwappedItemFromParentCalendar = modalData.isInherited && !modalData.swap;
      const dateKey = formatDate();
      const candidateFreshItem = programType === 'free-form-program'
        ? nonDateBasedProgramItems?.[modalData.dayNumber]?.exercises?.[modalData.itemIndex]
        : calendarItems?.[dateKey]?.exercises?.[modalData.itemIndex];
      const freshItem = (candidateFreshItem && candidateFreshItem.assignedId === modalData.assignedId)
        ? candidateFreshItem
        : null;
      const sourceItem = freshItem ? { ...modalData, ...freshItem } : modalData;
      const updateItem = {
        ...sourceItem,
        assignedId: modalData.assignedId,
        calendarId: modalData.calendarId,
        date: dateKey,
        order: modalData.itemIndex,
        sets: sourceItem.sets,
        coachCompletion: sourceItem.coachCompletion,
        eachSide: sourceItem.eachSide,
        type: modalData.type,
        trackPeakPower: sourceItem.trackPeakPower,
        restTime: sourceItem.restTime ? sourceItem.restTime : null,
      };

      if (programType === 'free-form-program') {
        updateItem.dayNumber = modalData.dayNumber;
      }
      if (modalData.type === 'tag') {
        updateItem.tagId = modalData.tag.id;
      } else {
        updateItem.exerciseId = modalData.exercise.id;
      }
      if (activeModal === 'addDescriptionModal') {
        if (text) {
          updateItem.description = text;
        }
        if (sourceItem.additionalInfo) {
          updateItem.additionalInfo = sourceItem.additionalInfo;
        }
      } else if (activeModal === 'addAdditionalInfoModal') {
        if (sourceItem.description) {
          updateItem.description = sourceItem.description;
        } else if (sourceItem.description === '') {
          delete updateItem.description;
        }
        if (!text) {
          delete updateItem.additionalInfo;
        }
        if (text) {
          updateItem.additionalInfo = text;
        }
      }
      if (sourceItem.pacing) {
        updateItem.pacing = sourceItem.pacing;
      } else if (sourceItem.pacing === null) {
        delete updateItem.pacing;
      }
      if (sourceItem.trackRepCount) {
        updateItem.trackRepCount = sourceItem.trackRepCount;
      }
      if (sourceItem.trackVolumeLoad) {
        updateItem.trackVolumeLoad = sourceItem.trackVolumeLoad;
      }
      if (sourceItem.repsOnly) {
        updateItem.repsOnly = sourceItem.repsOnly;
      }
      if (sourceItem.repType) {
        updateItem.repType = sourceItem.repType;
      }
      if (sourceItem.forceMaxUpdate) {
        updateItem.forceMaxUpdate = sourceItem.forceMaxUpdate;
      }
      if (sourceItem.maxTrackingDisabled) {
        updateItem.maxTrackingDisabled = sourceItem.maxTrackingDisabled;
      }
      if (sourceItem.tempo) {
        updateItem.tempo = sourceItem.tempo;
      }
      if (sourceItem.trackBarSpeed) {
        updateItem.trackBarSpeed = sourceItem.trackBarSpeed;
      }
      if (sourceItem.repIntensityUnit) {
        updateItem.repIntensityUnit = sourceItem.repIntensityUnit;
      }
      if (modalData.type === 'cardio' || modalData.type === 'lift' || modalData.type === 'tag') {
        if (sourceItem.completionOnly) {
          updateItem.completionOnly = sourceItem.completionOnly;
        }
      }
      if (programType === 'free-form-program') {
        const reorder = false;
        dispatch(updateSavedWorkoutItem(currentUser.accountCode, updateItem, modalData.itemIndex, modalData.dayNumber, reorder, {
          onSuccess: () => {
            closeModal();
          },
          onError: () => {},
        }));
      }
      if (programType === 'calendar') {
        if (nonSwappedItemFromParentCalendar) {
          const reorder = false;
          dispatch(swapProgrammedItemOnChildCalendar(currentUser.accountCode, updateItem, modalData.itemIndex, formatDate(), reorder, {
            onSuccess: () => {
              closeModal();
            },
            onError: () => {},
          }));
        } else {
          const reorder = false;
          dispatch(updateProgrammedCalendarItem(currentUser.accountCode, updateItem, modalData.itemIndex, formatDate(), reorder, {
            onSuccess: () => {
              closeModal();
            },
            onError: () => {},
          }));
        }
      }
    } else {
      if (activeModal === 'addAdditionalInfoModal') {
        dispatch(logReduxPresaveAddInfo(
          modalData.id,
          text,
        ));
      } else {
        dispatch(logReduxPresaveDescription(
          modalData.id,
          text,
        ));
      }
      closeModal();
    }
  };

  const handleRevertToOriginalDescription = () => {
    setText(decodeHtmlEntities(modalData?.exercise?.description));
  };

  return (
    <ModalContainer>
      <div className='remove-div'>
        <div style={{
          display: 'flex',
          alignItems: 'center',
        }}
        >
          <TextContainer>
            <TopTextAndX>
              <ModalTextTop>{activeModal === 'addAdditionalInfoModal' ? 'Add Additional Info' : 'Add Description'}</ModalTextTop>
              <div
                className='remove-div-icon'
                style={{
                  cursor: 'pointer',
                }}
                onClick={() => (!isCalendarItemUpdating ? closeModal() : null)}
              >
                <IcomoonReact
                  iconSet={iconSet}
                  size={15}
                  icon='remove'
                  color='grey'
                />
              </div>
            </TopTextAndX>
            {activeModal === 'addAdditionalInfoModal' && modalData?.title && (
              <ExerciseTitle title={modalData.title}>{modalData.title}</ExerciseTitle>
            )}
            <Subtext>
              Add specific instructions for this exercise
              {activeModal === 'addAdditionalInfoModal' ? ' (Max 255 characters)' : ''}
              .
            </Subtext>
          </TextContainer>
        </div>
      </div>
      {isCalendarItemUpdating ? (<SpinnerWrapper><Spinner /></SpinnerWrapper>) : (
        <>
          <TextArea
            value={text}
            onChange={(e) => {
              if (activeModal === 'addAdditionalInfoModal') {
                // Additional info has 255 character limit
                if (e.target.value.length <= 255) {
                  setText(e.target.value);
                }
              } else {
                // Description has no character limit
                setText(e.target.value);
              }
            }}
            autoFocus
            maxLength={activeModal === 'addAdditionalInfoModal' ? 255 : undefined}
          />
          {activeModal === 'addDescriptionModal' && modalData?.exercise?.description && text !== decodeHtmlEntities(modalData?.exercise?.description) && (
          <RevertTextContainer onClick={() => handleRevertToOriginalDescription()}>
            <IcomoonReact
              iconSet={iconSet}
              size={12}
              icon='restart'
              color='#77A7FF'
            />
            <Text fontSize='16px' color='#77A7FF'>Revert to original</Text>
          </RevertTextContainer>
          )}
          {showRequiredError && (
            <ErrorText>Description cannot be empty.</ErrorText>
          )}
        </>
      )}
      <ButtonsContainer>
        <NewCalendarButton
          disabled={showRequiredError}
          onClick={() => {
            if (!isCalendarItemUpdating && !showRequiredError) {
              handleSave();
            }
          }}
        >
          SAVE
        </NewCalendarButton>
        <ManageCalendarsButton onClick={() => (!isCalendarItemUpdating ? closeModal() : null)}>
          CANCEL
        </ManageCalendarsButton>
      </ButtonsContainer>
    </ModalContainer>
  );
};

AddDescriptionModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
};

export default AddDescriptionModal;
